import {
  AppointmentControllerService,
  AppointmentResponse,
  AppointmentUpdateRequest,
  UserShortLivedTokenControllerService
} from "@9amhealth/openapi";
import { isZoomLink } from "@9amhealth/shared";
import {
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal
} from "@internationalized/date";
import { Cubit } from "blac-next";
import React from "react";
import SanityService from "src/api/SanityService";
import { globalEvents } from "src/constants/globalEvents";
import {
  DocumentCheckedIcon,
  IconCalendarPlus,
  IconClock,
  IconVideoRecorder
} from "src/constants/icons";
import { OpenBrowser } from "src/hybrid/components/Browser";
import { DateFormats, dateLocal } from "src/lib/date";
import envVariables from "src/lib/envVariables";
import { getSupportedUserLanguage } from "src/lib/i18next";
import { isHybridApp } from "src/lib/platform";
import reportErrorSentry from "src/lib/reportErrorSentry";
import sanityQuery from "src/lib/sanityQuery";
import { toast, tracker, userState } from "src/state/state";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import type { TranslationKey } from "src/types/translationKey";
import {
  AppPopup,
  AppQueryPopupsController
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import { SupportedLabProvider } from "src/ui/components/AppQueryPopups/dialogs/LabInstructionsDialog";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import FilesCubit from "../FilesCubit/FilesCubit";
import { StorageController } from "../StorageBloc/StorageBloc";
import type = AppointmentResponse.type;
import status = AppointmentResponse.status;

export interface AppointmentsState {
  scheduledAppointments: AppointmentResponse[];
  loading: boolean;
}

export interface AppointmentSanityDetails {
  eventName: string;
  joiningInstructions: string;
  language: string;
  _type: string;
  type: string;
  _rev: string;
  avatar?: Avatar;
  _createdAt: string;
  eventContent: EventContent[];
  _id: string;
  title: string;
  _updatedAt: string;
}

export interface Avatar {
  _type: string;
  assetId: string;
  url: string;
}

export interface EventContent {
  contentTitle: string;
  _key: string;
  content: Content[];
}

export interface Content {
  level?: number;
  _type: string;
  style: string;
  _key: string;
  listItem?: string;
  children: Children[];
}

export interface Children {
  _type: string;
  marks: string[];
  text: string;
  _key: string;
}

export interface ActionConfig {
  label: TranslationKey;
  icon: React.ReactNode;
  onClick: () => void;
}

export interface AppointmentConfig {
  cardActions: ActionConfig[];
  detailActions: ActionConfig[];
}

export interface AppointmentTime {
  weekday: string;
  timeZone: string;
  startTime: string;
  endTime: string;
}

const DAY_IN_MINUTES = 24 * 60;

const HOUR_IN_MINUTES = 60;

class AppointmentsBloc extends Cubit<AppointmentsState> {
  static keepAlive = true;
  appointments: AppointmentResponse[] = [];

  constructor() {
    super({
      scheduledAppointments: [],
      loading: false
    });

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.appointments = [];
      this.emit({
        scheduledAppointments: [],
        loading: false
      });
      // Clear the cache entirely
      if (StorageController.activeUserId) {
        StorageController.removeItem(this.cacheKey);
      }
    });

    void this.getCachedAppointments();
    void this.loadAppointments();
  }

  addAppointment = (appointment: AppointmentResponse) => {
    const all = [appointment, ...this.appointments];
    const unique = all.filter(
      (a, i, self) => i === self.findIndex((t) => a.id === t.id)
    );
    this.appointments = unique;

    this.emit({
      ...this.state,
      scheduledAppointments: this.filterAppointments(unique)
    });
  };

  getAppointmentMetaDataValue = (
    appointment: AppointmentResponse,
    key: "requisition_file_id"
  ) => {
    const additionalData = appointment.additionalData as
      | Record<string, unknown>
      | undefined;
    if (!additionalData || typeof additionalData !== "object") {
      return undefined;
    }

    return additionalData[key];
  };

  createAddToCalendarAction = (appointment: AppointmentResponse) => {
    // only show add to calendar for hybrid app
    if (!isHybridApp()) {
      return null;
    }

    return {
      label: "appointment.action.label.addToCalendar",
      icon: <IconCalendarPlus />,
      onClick: () => {
        tracker.track(TrackEvent.AppointmentAddToCalClick, {
          type: TrackType.click,
          data: {
            appointmentId: appointment.id
          }
        });

        BlockingLoadingOverlayController.startLoading();
        void AppointmentsBloc.downloadIcsFile(appointment.id).finally(() => {
          BlockingLoadingOverlayController.endLoading();
        });
      }
    } satisfies ActionConfig;
  };

  createJoinCallAction = (
    appointment: AppointmentResponse
  ): ActionConfig | null => {
    const visible = isZoomLink(appointment.location);
    if (!visible) {
      return null;
    }

    return {
      label: "appointment.action.label.joinCall",
      icon: <IconVideoRecorder />,
      onClick: () => {
        tracker.track(TrackEvent.AppointmentJoinCallClick, {
          type: TrackType.click,
          data: {
            location: appointment.location
          }
        });

        if (!appointment.location) {
          return;
        }

        window.open(appointment.location, "_blank");
      }
    } satisfies ActionConfig;
  };

  getAppointmentById = async (
    id: string
  ): Promise<AppointmentResponse | undefined> => {
    const appointment = this.state.scheduledAppointments.find(
      (a) => a.id === id
    );
    if (appointment) {
      return appointment;
    }
    await this.loadAppointments();
    return this.state.scheduledAppointments.find((a) => a.id === id);
  };

  createRescheduleAction = (
    appointment: AppointmentResponse
  ): ActionConfig | null => {
    const visible = appointment.status === status.SCHEDULED;
    if (!visible) {
      return null;
    }
    const minutesToStart = this.minutesToStart(appointment);
    if (minutesToStart < HOUR_IN_MINUTES) {
      return null;
    }

    if (
      appointment.type === type.AT_HOME_LABS_GETLABS &&
      minutesToStart < DAY_IN_MINUTES
    ) {
      return null;
    }

    return {
      label: "appointment.action.label.reschedule",
      icon: <IconClock />,
      onClick: () => {
        tracker.track(TrackEvent.AppointmentRescheduleClick, {
          type: TrackType.click,
          data: {
            appointmentId: appointment.id
          }
        });

        AppQueryPopupsController.closePopup();
        AppQueryPopupsController.openPopup(AppPopup.scheduleAppointment, {
          onEvent: {
            [globalEvents.APPOINTMENT_RESCHEDULED]: () => {
              void this.loadAppointments();
            }
          },
          additionalParameters: {
            reschedule: appointment.id
          }
        });
      }
    } satisfies ActionConfig;
  };

  createShowRequisitionFileAction = (appointment: AppointmentResponse) => {
    const requisitionFileId = this.getAppointmentMetaDataValue(
      appointment,
      "requisition_file_id"
    ) as string | undefined;
    if (!requisitionFileId) {
      return null;
    }
    return {
      label: "appointment.action.label.showRequisition",
      icon: <DocumentCheckedIcon />,
      onClick: () => {
        tracker.track(TrackEvent.AppointmentShowRequisitionClick, {
          type: TrackType.click,
          data: {
            appointmentId: appointment.id
          }
        });
        void FilesCubit.startFileDownload(requisitionFileId);
      }
    } satisfies ActionConfig;
  };

  createAppointmentConfig = (
    appointment: AppointmentResponse
  ): AppointmentConfig => {
    const actionCalendar = this.createAddToCalendarAction(appointment);
    const actionJoinCall = this.createJoinCallAction(appointment);
    const actionReschedule = this.createRescheduleAction(appointment);
    const actionRequisitionFile =
      this.createShowRequisitionFileAction(appointment);

    const cardActions = [
      actionCalendar,
      actionJoinCall,
      actionReschedule
    ].filter((action) => action !== null);

    const detailActions = [
      actionCalendar,
      actionJoinCall,
      actionRequisitionFile,
      actionReschedule
    ].filter((action) => action !== null);

    return { cardActions, detailActions };
  };

  private setAppointmentsConfig = (appointments: AppointmentResponse[]) => {
    return appointments.map((appointment) => {
      const participantsWithoutUser = appointment.participants.filter(
        (p) => p.userId !== userState.state.userData?.id
      );
      return {
        ...appointment,
        participants: participantsWithoutUser,
        config: this.createAppointmentConfig(appointment)
      };
    });
  };

  cacheKey = "scheduled-appointments";
  public readonly getCachedAppointments = async () => {
    // Only load if we have an active user
    if (!StorageController.activeUserId) {
      return;
    }

    const cached = StorageController.getItemParsed<AppointmentResponse[]>(
      this.cacheKey
    );
    if (cached) {
      this.appointments = this.setAppointmentsConfig(cached);
      this.patch({
        scheduledAppointments: this.filterAppointments(this.appointments)
      });
    }
  };

  filterAppointments = (
    appointments: AppointmentResponse[],
    options: {
      /** Offset in minutes to consider an appointment expired, 15 = (now + 15 minutes) */
      expiredOffset?: number;
      /** If true, sort the appointments by start date */
      sort?: boolean;
      filter?: Partial<AppointmentResponse>;
    } = {}
  ) => {
    const {
      // Default to -15 minutes, which means the appointment is considered expired 15 minutes before the end date
      // expiredOffset = -15,
      expiredOffset = -15,
      filter = {
        status: status.SCHEDULED
      },
      sort = true
    } = options;
    const localTime = now(getLocalTimeZone());
    const offsetInMs = expiredOffset * 60000;

    let output = appointments;

    output = output.filter((appointment) => {
      const asZoned = parseAbsoluteToLocal(appointment.end);
      const isExpired = localTime.compare(asZoned) > offsetInMs;

      const filterMatch = Object.keys(filter).every((key) => {
        return (
          appointment[key as keyof AppointmentResponse] ===
          filter[key as keyof AppointmentResponse]
        );
      });

      return !isExpired && filterMatch;
    });

    if (sort) {
      output = output.toSorted((a, b) => {
        return parseAbsoluteToLocal(a.start).compare(
          parseAbsoluteToLocal(b.start)
        );
      });
    }

    return output;
  };

  loadAppointmentsPromise?: Promise<void>;

  public readonly loadAppointments = async () => {
    if (this.loadAppointmentsPromise) {
      return this.loadAppointmentsPromise;
    }

    this.loadAppointmentsPromise = new Promise((resolve) => {
      void AppointmentControllerService.getScheduledAppointments()
        .then(({ data }) => {
          const appointments = this.setAppointmentsConfig(data);
          this.appointments = appointments;
          this.patch({
            scheduledAppointments: this.filterAppointments(this.appointments)
          });
          StorageController.setItem(
            this.cacheKey,
            JSON.stringify(this.appointments)
          );
          resolve();
        })
        .finally(() => {
          this.loadAppointmentsPromise = undefined;
          resolve();
        });
    });

    return this.loadAppointmentsPromise;
  };

  minutesToStart = (appointment: AppointmentResponse): number => {
    const localTime = now(getLocalTimeZone());
    const startAbsolute = parseAbsoluteToLocal(appointment.start);
    const diffMs = startAbsolute.compare(localTime);
    const diffMinutes = Math.floor(diffMs / 1000 / 60);
    return diffMinutes;
  };

  cancelAppointment = async (appointment: AppointmentResponse) => {
    try {
      this.patch({ loading: true });

      tracker.track(TrackEvent.AppointmentCancelClick, {
        type: TrackType.click,
        data: {
          appointmentId: appointment.id,
          appointmentTitle: appointment.title
        }
      });

      await AppointmentControllerService.updateMemberAppointment(
        appointment.id,
        {
          status: AppointmentUpdateRequest.status.CANCELED,
          start: appointment.start,
          end: appointment.end,
          participantUserIds: appointment.participants.map((p) => p.userId)
        }
      );

      await this.loadAppointments();
    } catch (error) {
      toast.error("error_cancel_appointment");
      reportErrorSentry(error);
    } finally {
      this.patch({ loading: false });
    }
  };

  static getAppointmentReschedulingUrl = async (appointmentId: string) => {
    const { data } =
      await AppointmentControllerService.initiateRescheduling(appointmentId);

    const { schedulingUrl } = data;

    return schedulingUrl;
  };

  static downloadIcsFile = async (appointmentId: string) => {
    try {
      const res =
        await UserShortLivedTokenControllerService.retrieveShortLivedToken();

      const { accessToken } = res.data;
      const baseUrl = envVariables.API_BASE_URL;

      const fileUrl = `${baseUrl}/v1/appointments/${appointmentId}.ics?authToken=${accessToken}`;

      void OpenBrowser(fileUrl, {
        presentationStyle: "popover",
        useBaseUrl: false
      });
    } catch (error) {
      toast.show("error_failed_appointment_calendar");

      reportErrorSentry(error);
    }
  };

  static getAppointmentCmsDetails = async (
    type: AppointmentResponse.type | SupportedLabProvider
  ) => {
    const language = getSupportedUserLanguage();

    const appointmentDetails: AppointmentSanityDetails[] =
      await SanityService.fetchSanityData(
        sanityQuery.appointmentDetailsByType(type, language)
      );

    return appointmentDetails[0];
  };

  public getAppointment = (
    id: string,
    appointments: AppointmentResponse[]
  ): AppointmentResponse | undefined => {
    return appointments.find((appointment) => appointment.id === id);
  };

  getAppointmentTime = (start?: string, end?: string): AppointmentTime => {
    const userLanguage = getSupportedUserLanguage();

    const weekday = dateLocal(start, { locale: userLanguage }).format(
      DateFormats.DISPLAY_WEEKDAY_MONTH_DAY
    );
    const timeZone = dateLocal(start, { locale: userLanguage }).format("z");
    const startTime = dateLocal(start, { locale: userLanguage }).format(
      DateFormats.DISPLAY_TIME_ALT
    );
    const endTime = dateLocal(end, { locale: userLanguage }).format(
      DateFormats.DISPLAY_TIME_ALT
    );

    return {
      weekday,
      timeZone,
      startTime,
      endTime
    };
  };
}

export default AppointmentsBloc;
